/**
 * A react/aprhodite version of our grid-of-people team page content.
 *
 * This is currently only used for the below-the-fold part of the leadership
 * team / board page.
 */

import {css, StyleSheet} from "aphrodite";
import classNames from "classnames";
import * as React from "react";

import {useTimeout} from "@khanacademy/wonder-blocks-timing";
import {color} from "@khanacademy/wonder-blocks-tokens";
import {staticUrl} from "@khan/urls";

import type {TeamCardProps, TeamPageSectionProps} from "./types.ts";

/**
 * A card for a team member on one of our team pages.
 *
 * TODO(jvoll): consider consolidating this with TeamMemberCard in
 * team-page-body.jsx, which behaves slightly differently.
 *
 * TODO(colin): it currently doesn't make a whole lot of sense to have this be
 * in a separate file because it depends on the legacy code still, which is
 * loaded in the parent.  If we completely reactify, move this to a separate
 * file and add a separate fixture.
 */
function TeamCard(props: TeamCardProps) {
    const {
        translatedDescription,
        name,
        photoURL,
        translatedTitle,
        contactLinks,
        onCardHover,
    } = props;

    const cardInfoRef = React.useRef<HTMLDivElement>(null);

    // Set the height of the portrait container to fit the card info
    const setHeight = React.useCallback(() => {
        const infoHeight = cardInfoRef.current?.clientHeight;
        if (infoHeight) {
            const portraitContainer = document.querySelector(
                ".portrait-container",
            ) as HTMLElement;
            portraitContainer.style.paddingBottom = `${infoHeight}px`;
        }
    }, []);

    const {clear, set: startTimeout} = useTimeout(setHeight, 50);

    const handleCardMouseEnter = () => {
        onCardHover && onCardHover(true);

        startTimeout();
    };

    const handleCardMouseLeave = () => {
        onCardHover && onCardHover(false);

        const portraitContainer = document.querySelector(
            ".portrait-container",
        ) as HTMLElement;
        portraitContainer.style.paddingBottom = "initial";

        clear();
    };

    return (
        // eslint-disable-next-line jsx-a11y/no-static-element-interactions -- TODO: Address a11y error
        <div
            className="team-card"
            onMouseEnter={handleCardMouseEnter}
            onMouseLeave={handleCardMouseLeave}
        >
            <div className="card-photo">
                <img alt={name} src={staticUrl(photoURL)} />
            </div>
            <div className="card-info" ref={cardInfoRef}>
                <h2 className="pulls">{name}</h2>

                {/* TODO(colin): we shouldn't be using h2 and h4 at the same
                    level, but the shared css that styles this and other pages
                    assumes this is the way it's structured.  Fix this. */}
                {translatedTitle && (
                    <h4 className={css(styles.teamMemberTitle)}>
                        {translatedTitle}
                    </h4>
                )}

                {translatedDescription.map((para, idx) => (
                    <p key={idx}>{para}</p>
                ))}

                {contactLinks && (
                    <p className="contact-links">
                        {contactLinks.twitterURL && (
                            <a
                                aria-label="Twitter"
                                className="contact-twitter"
                                href={contactLinks.twitterURL}
                            />
                        )}
                    </p>
                )}
            </div>
        </div>
    );
}

/**
 * A single section of our team page, comprising a title for the section and an
 * array of TeamCards for members of that section of the page.
 */
function TeamPageSection(props: TeamPageSectionProps) {
    const {people, translatedSectionTitle, showDividerBottom, onCardHover} =
        props;

    return (
        <section className={css(styles.teamPageSection)}>
            <h1 className={css(styles.teamPageSectionTitle)}>
                {translatedSectionTitle}
            </h1>
            <div>
                {people.map((person, idx) => {
                    return (
                        <TeamCard
                            key={person.name}
                            {...person}
                            onCardHover={onCardHover}
                        />
                    );
                })}
            </div>
            {showDividerBottom && (
                <hr className={css(styles.teamPageSectionDivider)} />
            )}
        </section>
    );
}

type TeamPageProps = {
    sections: Array<TeamPageSectionProps>;
};

/**
 * A possibly multiple-section team page containing one or more delimited
 * TeamPageSections.
 *
 * This currently only renders below-the-fold content on the page.
 * TODO(colin): port the team page header and nav to react/aphrodite and include
 * them here.
 */
function TeamPage(props: TeamPageProps) {
    const {sections} = props;
    const [isHovered, setIsHovered] = React.useState(false);

    return (
        <div
            className={classNames({
                "portrait-container": true,
                "over-team-card": isHovered,
            })}
        >
            {sections.map((section, idx) => {
                return (
                    <TeamPageSection
                        key={section.translatedSectionTitle}
                        showDividerBottom={idx !== sections.length - 1}
                        {...section}
                        onCardHover={setIsHovered}
                    />
                );
            })}
        </div>
    );
}

const styles = StyleSheet.create({
    teamMemberTitle: {
        textAlign: "center",
    },
    teamPageSection: {
        alignItems: "flex-start",
        display: "flex",
        flexDirection: "column",
        margin: "0 auto 32px",
        maxWidth: "100%",
        // @ts-expect-error - TS2322 - not assignable to type 'CSSProperties'.
        "@media screen and (min-width: 445px)": {
            width: 445,
        },
        "@media screen and (min-width: 660px)": {
            width: 660,
        },
        "@media screen and (min-width: 875px)": {
            width: 875,
        },
        "@media screen and (min-width: 1100px)": {
            width: 1100,
        },
    },
    teamPageSectionDivider: {
        width: "95%",
        margin: "298px auto 0",
    },
    teamPageSectionTitle: {
        color: color.offBlack,
        fontSize: 23,
        textAlign: "center",
        width: "100%",
        // @ts-expect-error - TS2322 - not assignable to type 'CSSProperties'.
        "@media screen and (min-width: 445px)": {
            textAlign: "left",
            marginLeft: 15,
        },
    },
});

export default TeamPage;
